import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    bil_id: '',
    service_type: '',
    service_dato: '',
    beskrivelse: '',
    status: 'Planlagt'
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch('/api/services');
      const data = await response.json();
      setServices(data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingService ? `/api/services/${editingService.id}` : '/api/services';
      const method = editingService ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setShowModal(false);
        setEditingService(null);
        setFormData({
          bil_id: '',
          service_type: '',
          service_dato: '',
          beskrivelse: '',
          status: 'Planlagt'
        });
        fetchServices();
      }
    } catch (error) {
      console.error('Error saving service:', error);
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData(service);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Er du sikker på, at du vil slette denne service?')) {
      try {
        const response = await fetch(`/api/services/${id}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          fetchServices();
        }
      } catch (error) {
        console.error('Error deleting service:', error);
      }
    }
  };

  const filteredServices = services.filter(service =>
    service.beskrivelse.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (service.bil_internt_nummer && service.bil_internt_nummer.toLowerCase().includes(searchTerm.toLowerCase())) ||
    service.service_type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Planlagt':
        return 'bg-blue-100 text-blue-800';
      case 'I gang':
        return 'bg-yellow-100 text-yellow-800';
      case 'Afsluttet':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-falck-blue"></div>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 md:px-6 py-2 sm:py-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Services</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-falck-blue text-white px-4 py-2 rounded-lg hover:bg-falck-dark-blue transition-colors flex items-center"
        >
          <FaPlus className="mr-2" />
          Tilføj service
        </button>
      </div>

      {/* Søgefelt */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Søg efter services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-falck-blue bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {/* Services tabel */}
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow overflow-hidden">
        <div className="scroll-fade-x"><div className="scroll-area">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Service type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Bil
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Service dato
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Beskrivelse
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Handlinger
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredServices.map((service) => (
              <tr key={service.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {service.service_type}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {service.bil_internt_nummer || 'Ikke angivet'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {service.service_dato ? new Date(service.service_dato).toLocaleDateString('da-DK') : 'Ikke angivet'}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 max-w-xs truncate">
                    {service.beskrivelse}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(service.status)}`}>
                    {service.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => handleEdit(service)}
                    className="text-falck-blue hover:text-falck-dark-blue mr-3"
                  >
                    <FaEdit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(service.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <FaTrash className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div></div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingService ? 'Rediger service' : 'Tilføj ny service'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Service type
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.service_type}
                    onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                    placeholder="f.eks. Service, Reparation, Eftersyn"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Bil (internt nummer)
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.bil_id}
                    onChange={(e) => setFormData({...formData, bil_id: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                    placeholder="Indtast internt nummer på bilen"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Service dato
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.service_dato}
                    onChange={(e) => setFormData({...formData, service_dato: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Beskrivelse
                  </label>
                  <textarea
                    required
                    value={formData.beskrivelse}
                    onChange={(e) => setFormData({...formData, beskrivelse: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                    rows="3"
                    placeholder="Beskriv serviceopgaven..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-falck-blue focus:border-falck-blue"
                  >
                    <option value="Planlagt">Planlagt</option>
                    <option value="I gang">I gang</option>
                    <option value="Afsluttet">Afsluttet</option>
                  </select>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingService(null);
                      setFormData({
                        bil_id: '',
                        service_type: '',
                        service_dato: '',
                        beskrivelse: '',
                        status: 'Planlagt'
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuller
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-falck-blue hover:bg-falck-dark-blue"
                  >
                    {editingService ? 'Opdater' : 'Tilføj'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Services;
